import { useState, useEffect, useCallback, useRef } from 'react';
import { useNetworkStatus } from './useNetworkStatus';
import { useOfflineStoresEmpty } from './useOfflineStoresEmpty';
import { ALL_IMPORTANT_STORES } from '../service-worker-utility';

/**
 * Custom hook that combines network status and offline data availability
 * to determine the application's data status
 * 
 * @returns Object containing network and data availability status
 */
export const useOfflineDataStatus = () => {
  const { isOnline } = useNetworkStatus();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { isLoading, isEmpty, error, storeStatus } = useOfflineStoresEmpty(ALL_IMPORTANT_STORES, refreshTrigger);
  const [hasOfflineData, setHasOfflineData] = useState(false);
  const wasOfflinePreviously = useRef(false);

  // Function to trigger a refresh of the store status - defined BEFORE using it in useEffect
  const refreshOfflineStatus = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setHasOfflineData(!isEmpty);
    }
  }, [isLoading, isEmpty]);

  // Trigger a refresh when network status changes from offline to online
  useEffect(() => {
    if (isOnline && wasOfflinePreviously.current) {
      // We were offline before, but now we're online - refresh the store status
      
      refreshOfflineStatus();
    }
    
    // Track previous online status
    wasOfflinePreviously.current = !isOnline;
  }, [isOnline, refreshOfflineStatus]);

  return {
    isOnline,
    isLoading,
    hasOfflineData,
    error,
    storeStatus,
    refreshOfflineStatus
  };
}; 